import React, { useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../../utils/constants";
import { authUrl } from "../../utils/auth";

// Add / edit an arbitrator. Every box is sent: an emptied box clears that detail. Pictures: PNG or JPG up to 5 MB,
// kept unless replaced or removed. The same checks run on the server; its messages are shown next to the boxes.

const MAX_PICTURE_MB = 5;
const PICTURE_TYPES = ["image/png", "image/jpeg"];
const PICTURES = [
  { kind: "photo", field: "Photo", label: "Photo", hint: "Shown on this screen only." },
  { kind: "signature", field: "Sign", label: "Signature", hint: "Printed on the letters. Needed before any letter can be made." },
  { kind: "stamp", field: "Stamp", label: "Stamp", hint: "Printed where a document's Word file has an arbitrator stamp." },
];

const toDateBox = (value) => (value ? new Date(value).toISOString().slice(0, 10) : "");

const fromRecord = (r) => ({
  Arb_name: r?.arbName || "",
  Education: r?.education || "",
  Designation: r?.designation || "",
  Post_qualification: r?.postQualification || "",
  Address: r?.address || "",
  Pin: r?.pin || "",
  Contact: r?.contactNo || "",
  Email_id: r?.emailId || "",
  Firm_name: r?.firmName || "",
  Passing_Year: r?.passingYear != null ? String(r.passingYear) : "",
  Fees: r?.fees != null ? String(r.fees) : "",
  experience_date: toDateBox(r?.experienceDate),
  Arb_start_date: toDateBox(r?.arbStartDate),
});

const today = new Date().toISOString().slice(0, 10);
const thisYear = new Date().getFullYear();

// Same rules as the server, so mistakes show before saving
function checkForm(form) {
  const errors = {};
  if (!form.Arb_name.trim()) errors.Arb_name = "Name is required";
  const phone = form.Contact.replace(/[\s-]/g, "").replace(/^(\+91|91|0)(?=\d{10}$)/, "");
  if (form.Contact.trim() && !/^[6-9]\d{9}$/.test(phone)) errors.Contact = "Enter a 10-digit mobile number";
  if (form.Email_id.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.Email_id.trim())) errors.Email_id = "Email is not valid";
  if (form.Pin.trim() && !/^\d{6}$/.test(form.Pin.trim())) errors.Pin = "Pincode must be 6 digits";
  if (form.Passing_Year.trim() && !(/^\d{4}$/.test(form.Passing_Year.trim()) && +form.Passing_Year >= 1940 && +form.Passing_Year <= thisYear)) {
    errors.Passing_Year = `A year between 1940 and ${thisYear}`;
  }
  if (form.Fees.trim() && !/^\d+(\.\d{1,2})?$/.test(form.Fees.trim())) errors.Fees = "An amount in rupees, e.g. 5000";
  if (form.experience_date && form.experience_date > today) errors.experience_date = "Can't be in the future";
  if (form.Arb_start_date && form.Arb_start_date > today) errors.Arb_start_date = "Can't be in the future";
  if (form.Post_qualification.length > 3000) errors.Post_qualification = "At most 3000 characters";
  return errors;
}

// Server field names → form boxes
const SERVER_FIELD = { Contact: "Contact", Email_id: "Email_id", Pin: "Pin", Passing_Year: "Passing_Year", Fees: "Fees", Arb_name: "Arb_name" };

const Field = ({ label, required, error, hint, children }) => (
  <div className="mb-2">
    <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.82rem" }}>
      {label} {required && <span className="text-danger">*</span>}
    </label>
    {children}
    {error ? <div className="text-danger" style={{ fontSize: "0.75rem" }}>{error}</div> : hint && <div className="text-muted" style={{ fontSize: "0.75rem" }}>{hint}</div>}
  </div>
);

const AddArbitrator = ({ onSave, onCancel, initialData, version }) => {
  const isEdit = Boolean(initialData?.arbId);
  const start = useMemo(() => fromRecord(initialData), [initialData]);
  const [form, setForm] = useState(start);
  const [files, setFiles] = useState({}); // kind → File
  const [previews, setPreviews] = useState({}); // kind → object URL of the new file
  const [remove, setRemove] = useState({}); // kind → true
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => () => Object.values(previews).forEach((url) => URL.revokeObjectURL(url)), [previews]);

  const changed =
    JSON.stringify(form) !== JSON.stringify(start) || Object.keys(files).length > 0 || Object.values(remove).some(Boolean);

  // Warn before closing the browser tab with unsaved changes
  useEffect(() => {
    const warn = (e) => {
      if (changed && !saving) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [changed, saving]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((err) => ({ ...err, [key]: undefined }));
  };

  const pickFile = (kind) => (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!PICTURE_TYPES.includes(file.type)) {
      setErrors((err) => ({ ...err, [kind]: "Choose a PNG or JPG picture" }));
      return;
    }
    if (file.size > MAX_PICTURE_MB * 1024 * 1024) {
      setErrors((err) => ({ ...err, [kind]: `The picture is larger than ${MAX_PICTURE_MB} MB` }));
      return;
    }
    setErrors((err) => ({ ...err, [kind]: undefined }));
    setFiles((f) => ({ ...f, [kind]: file }));
    setPreviews((p) => ({ ...p, [kind]: URL.createObjectURL(file) }));
    setRemove((r) => ({ ...r, [kind]: false }));
  };

  const undoPicture = (kind) => {
    setFiles(({ [kind]: _, ...rest }) => rest);
    setPreviews(({ [kind]: url, ...rest }) => {
      if (url) URL.revokeObjectURL(url);
      return rest;
    });
    setRemove((r) => ({ ...r, [kind]: false }));
  };

  const cancel = () => {
    if (changed && !window.confirm("Leave without saving your changes?")) return;
    onCancel();
  };

  const save = async (e) => {
    e.preventDefault();
    setServerError("");
    const found = checkForm(form);
    setErrors(found);
    if (Object.keys(found).length) return;
    if (remove.signature && !window.confirm("Remove the signature? Letters for this arbitrator can't be made until a new one is uploaded.")) return;

    const data = new FormData();
    if (isEdit) data.append("Arb_id", initialData.arbId);
    Object.entries(form).forEach(([key, value]) => data.append(key, value.trim ? value.trim() : value));
    PICTURES.forEach(({ kind, field }) => files[kind] && data.append(field, files[kind]));
    data.append("Remove", Object.keys(remove).filter((k) => remove[k] && !files[k]).join(","));

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/${isEdit ? "UpArb" : "Arb"}`, { method: "POST", body: data });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Field mistakes from the server go next to their boxes; anything else at the top
        const fieldErrors = {};
        (body.errors || []).forEach(({ path, message }) => {
          if (SERVER_FIELD[path]) fieldErrors[SERVER_FIELD[path]] = message;
        });
        setErrors((err) => ({ ...err, ...fieldErrors }));
        const rest = (body.errors || []).filter(({ path }) => !SERVER_FIELD[path]).map((x) => x.message);
        setServerError(rest.length ? rest.join(". ") : Object.keys(fieldErrors).length ? "" : body.message || `Saving failed (${res.status})`);
        return;
      }
      onSave(body);
    } catch (err) {
      setServerError(`Could not reach the server: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const currentUrl = (kind) => authUrl(`${API_BASE_URL}/api/arbitrator/${initialData.arbId}/picture/${kind}?v=${version}`);
  const prints = [form.Arb_name.trim(), [form.Education.trim(), form.Designation.trim()].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const box = (key, props = {}) => (
    <input className={`form-control form-control-sm ${errors[key] ? "is-invalid" : ""}`} value={form[key]} onChange={set(key)} {...props} />
  );

  return (
    <form onSubmit={save} className="border rounded customShadow p-3" noValidate>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="m-0">{isEdit ? `Edit ${initialData.arbName}` : "Add Arbitrator"}</h5>
        {isEdit && (
          <small className="text-muted">
            {initialData.openCases} open case(s) · {initialData.active ? "Active" : "Inactive"}
          </small>
        )}
      </div>

      {serverError && <div className="alert alert-danger py-2" style={{ fontSize: "0.85rem", whiteSpace: "pre-line" }}>{serverError}</div>}

      <div className="p-2 mb-3 rounded" style={{ background: "#e7f1ff", fontSize: "0.82rem" }}>
        Prints on letters as: <strong>{prints || "—"}</strong>
      </div>

      <h6 className="mt-2" style={{ fontSize: "0.9rem" }}>Printed on letters</h6>
      <div className="row">
        <div className="col-md-6">
          <Field label="Name (as printed)" required error={errors.Arb_name} hint='e.g. "Adv. Suresh S. Kasture". For capitals, use All caps in the Word file.'>
            {box("Arb_name", { maxLength: 120, autoFocus: !isEdit })}
          </Field>
        </div>
        <div className="col-md-2">
          <Field label="Qualification" hint="e.g. LL.B.">{box("Education", { maxLength: 100 })}</Field>
        </div>
        <div className="col-md-4">
          <Field label="Description" hint="e.g. (Retd. Dist. Judge)">{box("Designation", { maxLength: 300 })}</Field>
        </div>
        <div className="col-12">
          <Field
            label="Experience"
            error={errors.Post_qualification}
            hint={`Printed in the acceptance letter under "Prior Experience". ${form.Post_qualification.length}/3000`}
          >
            <textarea
              className={`form-control form-control-sm ${errors.Post_qualification ? "is-invalid" : ""}`}
              rows={3}
              maxLength={3000}
              value={form.Post_qualification}
              onChange={set("Post_qualification")}
            />
          </Field>
        </div>
        <div className="col-md-9">
          <Field label="Chamber address" hint="Printed as the arbitrator's office, e.g. Chamber No.1, H Block, …">
            <textarea className="form-control form-control-sm" rows={2} maxLength={500} value={form.Address} onChange={set("Address")} />
          </Field>
        </div>
        <div className="col-md-3">
          <Field label="Pincode" error={errors.Pin}>{box("Pin", { inputMode: "numeric", maxLength: 6 })}</Field>
        </div>
      </div>

      <h6 className="mt-2" style={{ fontSize: "0.9rem" }}>Contact</h6>
      <div className="row">
        <div className="col-md-4">
          <Field label="Mobile number" error={errors.Contact}>{box("Contact", { inputMode: "tel", maxLength: 16 })}</Field>
        </div>
        <div className="col-md-5">
          <Field label="Email" error={errors.Email_id}>{box("Email_id", { type: "email", maxLength: 120 })}</Field>
        </div>
        <div className="col-md-3">
          <Field label="Firm">{box("Firm_name", { maxLength: 120 })}</Field>
        </div>
      </div>

      <h6 className="mt-2" style={{ fontSize: "0.9rem" }}>Other details</h6>
      <div className="row">
        <div className="col-md-3">
          <Field label="Passing year" error={errors.Passing_Year}>{box("Passing_Year", { inputMode: "numeric", maxLength: 4 })}</Field>
        </div>
        <div className="col-md-3">
          <Field label="Fees (₹)" error={errors.Fees}>{box("Fees", { inputMode: "decimal", maxLength: 12 })}</Field>
        </div>
        <div className="col-md-3">
          <Field label="Enrolled on" error={errors.experience_date}>{box("experience_date", { type: "date", max: today })}</Field>
        </div>
        <div className="col-md-3">
          <Field label="Arbitrator since" error={errors.Arb_start_date}>{box("Arb_start_date", { type: "date", max: today })}</Field>
        </div>
      </div>

      <h6 className="mt-2" style={{ fontSize: "0.9rem" }}>Pictures (PNG or JPG, up to {MAX_PICTURE_MB} MB)</h6>
      <div className="row">
        {PICTURES.map(({ kind, label, hint }) => {
          const hasCurrent = isEdit && initialData.pictures?.[kind];
          const showing = previews[kind] || (hasCurrent && !remove[kind] ? currentUrl(kind) : null);
          return (
            <div className="col-md-4 mb-2" key={kind}>
              <div className="border rounded p-2 h-100">
                <div className="fw-semibold" style={{ fontSize: "0.82rem" }}>{label}</div>
                <div className="text-muted mb-1" style={{ fontSize: "0.72rem" }}>{hint}</div>
                <div className="d-flex align-items-center justify-content-center bg-light rounded mb-2" style={{ height: 90 }}>
                  {showing ? (
                    <img src={showing} alt={label} style={{ maxHeight: 84, maxWidth: "100%", objectFit: "contain" }} />
                  ) : (
                    <span className={kind === "signature" ? "text-danger" : "text-muted"} style={{ fontSize: "0.78rem" }}>
                      {remove[kind] ? "Will be removed when you save" : "Not uploaded"}
                    </span>
                  )}
                </div>
                {previews[kind] && <div className="text-success mb-1" style={{ fontSize: "0.72rem" }}>New picture – saved when you click Save</div>}
                {errors[kind] && <div className="text-danger mb-1" style={{ fontSize: "0.72rem" }}>{errors[kind]}</div>}
                <div className="d-flex gap-1 flex-wrap">
                  <label className="btn btn-sm btn-outline-primary mb-0">
                    {showing || hasCurrent ? "Replace" : "Upload"}
                    <input type="file" accept="image/png,image/jpeg" hidden onChange={pickFile(kind)} />
                  </label>
                  {(previews[kind] || remove[kind]) && (
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => undoPicture(kind)}>
                      Undo
                    </button>
                  )}
                  {hasCurrent && !remove[kind] && !previews[kind] && (
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setRemove((r) => ({ ...r, [kind]: true }))}>
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="d-flex justify-content-end gap-2 mt-3">
        <button type="button" className="btn btn-secondary btn-sm" onClick={cancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="custBtn px-4" style={{ fontSize: "0.85rem", padding: "6px 14px" }} disabled={saving || (isEdit && !changed)}>
          {saving ? "Saving…" : isEdit ? "Save changes" : "Add Arbitrator"}
        </button>
      </div>
    </form>
  );
};

export default AddArbitrator;
